import { useQuery } from '@tanstack/react-query'
import { useRouterState } from '@tanstack/react-router'
import { useState, type JSX } from 'react'

import { Button } from '../design-system/button.tsx'
import {
  ALERT_REPORTS_QUERY_KEY,
  fetchAlertReports
} from '../lib/alert-rules-api.ts'
import { AlertSheet } from './alert-sheet.tsx'

/**
 * AlertMe — the Alert me button at the top of every alertable report page.
 *
 * The root layout renders it above every page. It finds the page by matching
 * the path against the paths `GET /alert-reports` lists, so no page declares
 * anything: a report the backend declares is alertable once it exists. On a
 * path the catalogue does not list, and while the catalogue loads or fails,
 * it renders nothing.
 *
 * The catalogue changes only with a deploy, so it loads once per session.
 *
 * @returns The button and its sheet, or nothing.
 */
export function AlertMe(): JSX.Element | null {
  const pathname = useRouterState({ select: state => state.location.pathname })
  return <PageAlertMe key={pathname} pathname={pathname} />
}

/** One path owns one sheet and one draft. */
function PageAlertMe({ pathname }: { pathname: string }): JSX.Element | null {
  const [open, setOpen] = useState(false)
  const catalogue = useQuery({
    queryKey: ALERT_REPORTS_QUERY_KEY,
    queryFn: fetchAlertReports,
    staleTime: Infinity,
    retry: false
  })
  const report = catalogue.data?.reports.find(item => item.path === pathname)
  if (!report) {
    return null
  }

  return (
    <div data-testid="alert-me-bar" className="flex justify-end">
      <Button
        data-testid="alert-me"
        variant="outline"
        onClick={() => setOpen(true)}
      >
        Alert me
      </Button>
      <AlertSheet report={report} open={open} onOpenChange={setOpen} />
    </div>
  )
}
